import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Paper, Typography, Alert, CircularProgress, Button } from '@mui/material';
import { validateMagicLink } from '../../../app/api';
import { setToken, isAuthenticated } from '../../../app/auth';
import { useTranslation } from '../../../app/LanguageContext';
import ThemeModeToggle from '../../core/public/ThemeModeToggle';

const VerifyPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { translate } = useTranslation();
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');

  const email = searchParams.get('email');
  const code = searchParams.get('code');

  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/', { replace: true });
      return;
    }
    if (!email || !code) {
      setStatus('error');
      setError(translate('auth.magic_link_invalid_link'));
      return;
    }
    let cancelled = false;
    const run = async () => {
      try {
        const data = await validateMagicLink(email, code);
        if (cancelled) return;
        if (data?.data?.token) {
          setToken(data.data.token);
          setStatus('success');
          navigate('/', { replace: true });
        } else {
          setStatus('error');
          setError(translate('auth.invalid_credentials'));
        }
      } catch (err) {
        if (!cancelled) {
          setStatus('error');
          setError(err.message || translate('auth.invalid_credentials'));
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [email, code, navigate, translate]);

  if (status === 'error') {
    return (
      <>
      <Box sx={{ position: 'fixed', top: { xs: 8, sm: 12 }, right: { xs: 8, sm: 16 }, zIndex: 10 }}>
        <ThemeModeToggle />
      </Box>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 2,
        }}
      >
        <Paper sx={{ maxWidth: 400, width: '100%', p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {translate('auth.magic_link_verify_error_title')}
          </Typography>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button fullWidth variant="contained" onClick={() => navigate('/login', { replace: true })}>
            {translate('auth.magic_link_go_login')}
          </Button>
        </Paper>
      </Box>
      </>
    );
  }

  return (
    <>
    <Box sx={{ position: 'fixed', top: { xs: 8, sm: 12 }, right: { xs: 8, sm: 16 }, zIndex: 10 }}>
      <ThemeModeToggle />
    </Box>
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Paper sx={{ maxWidth: 400, width: '100%', p: 3, textAlign: 'center' }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography>{translate('auth.magic_link_verify_loading')}</Typography>
      </Paper>
    </Box>
    </>
  );
};

export default VerifyPage;
